import * as THREE from 'three';

/**
 * Per-plane geometry: a first-person COCKPIT view (cowling/spinner + the wing slivers
 * the pilot sees at the frame edges) and a full EXTERIOR model (shown when shot down).
 *
 * Silhouettes are deliberately distinct:
 *   - fokker   : triplane, rotary drum cowl (built in PlayerPlane.ts)
 *   - albatros : biplane, inline engine, long pointed spinner, sleek plywood fuselage
 *   - pfalz    : biplane, inline engine, rounded silver fuselage, blunt spinner
 *   - sopwith  : biplane, rotary drum cowl, British khaki with roundels
 */

export interface PlaneColors { cowling: number; wing: number; }

/* ------------------------------------------------------------------ */
/* Small shared helpers                                                */
/* ------------------------------------------------------------------ */

function airfoilWing(span: number, chord: number, thick: number, mat: THREE.Material, trimMat: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const shape = new THREE.Shape();
  const N = 24;
  shape.moveTo(-0.5 * chord, 0);
  for (let i = 1; i <= N; i++) {
    const t = i / N;
    const y = thick * (0.2969 * Math.sqrt(t) - 0.126 * t - 0.3516 * t * t + 0.2843 * t * t * t - 0.1015 * t * t * t * t);
    shape.lineTo((-0.5 + t) * chord, y);
  }
  for (let i = N - 1; i >= 0; i--) {
    const t = i / N;
    const y = -thick * 0.55 * (0.2969 * Math.sqrt(t) - 0.126 * t - 0.3516 * t * t + 0.2843 * t * t * t - 0.1015 * t * t * t * t);
    shape.lineTo((-0.5 + t) * chord, y);
  }
  shape.closePath();
  const stations = 20;
  const prof = shape.getPoints(28);
  const vps = prof.length;
  const pos: number[] = []; const idx: number[] = [];
  const half = span / 2;
  for (let s = 0; s <= stations; s++) {
    const x = -half + (s / stations) * span;
    const tip = Math.sqrt(Math.max(0, 1 - Math.pow(x / half, 2) * 0.9));
    for (const p of prof) pos.push(x, p.y * tip, p.x * tip);
  }
  for (let s = 0; s < stations; s++) {
    for (let i = 0; i < vps - 1; i++) {
      const a = s * vps + i, b = s * vps + i + 1, c = (s + 1) * vps + i + 1, d = (s + 1) * vps + i;
      idx.push(a, b, c, a, c, d);
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geom.setIndex(idx);
  geom.computeVertexNormals();
  g.add(new THREE.Mesh(geom, mat));
  const edge = new THREE.Mesh(new THREE.BoxGeometry(span * 0.94, thick * 0.9, chord * 0.13, 24, 1, 1), trimMat);
  edge.position.z = chord * 0.42;
  g.add(edge);
  return g;
}

function propBlade(len: number, w: number): THREE.BufferGeometry {
  const st = 10; const pos: number[] = []; const idx: number[] = [];
  for (let i = 0; i <= st; i++) {
    const t = i / st; const x = (t - 0.5) * len;
    const ww = w * Math.sin(Math.PI * t) * 0.95 + w * 0.12;
    const tw = (1 - t) * 0.5; const th = 0.05;
    pos.push(x, th, -ww / 2 + Math.sin(tw) * th);
    pos.push(x, th, ww / 2 + Math.sin(tw) * th);
    pos.push(x, -th, ww / 2 - Math.sin(tw) * th);
    pos.push(x, -th, -ww / 2 - Math.sin(tw) * th);
  }
  for (let i = 0; i < st; i++) {
    const b = i * 4;
    for (let k = 0; k < 4; k++) { const a = b + k, bb = b + (k + 1) % 4, c = b + 4 + (k + 1) % 4, d = b + 4 + k; idx.push(a, bb, c, a, c, d); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}

function makeProp(twist: number, mat: THREE.Material, hubMat: THREE.Material): THREE.Group {
  const prop = new THREE.Group();
  const bg = propBlade(2.3, twist);
  const b1 = new THREE.Mesh(bg, mat); const b2 = new THREE.Mesh(bg, mat); b2.rotation.z = Math.PI / 2;
  prop.add(b1, b2);
  prop.add(new THREE.Mesh(new THREE.SphereGeometry(0.18, 24, 16), hubMat));
  return prop;
}

/** Spinning-propeller blur for the cockpit view. The old version was a flat opaque disc that just
 *  greyed out the forward view. This is a translucent radial-streak texture (faint sweeping blades,
 *  transparent at the centre and feathered to nothing at the rim) on a disc that is SPUN each frame
 *  (tagged userData.propDisc so the plane's update rotates it) — so it reads as a turning prop, not
 *  a static smudge, and you can see through it. */
let _propTex: THREE.CanvasTexture | null = null;
function propBlurTexture(): THREE.CanvasTexture {
  if (_propTex) return _propTex;
  const S = 256, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, S, S);
  const cx = S / 2, cy = S / 2, R = S / 2;
  // A few motion-blurred blades as translucent swept wedges — bright enough to actually read as a
  // spinning disc, but feathered to transparent at the centre and rim so you still see through it.
  for (let i = 0; i < 3; i++) {
    const a0 = (i / 3) * Math.PI * 2;
    const grad = g.createRadialGradient(cx, cy, R * 0.1, cx, cy, R);
    grad.addColorStop(0, 'rgba(30,30,34,0)');
    grad.addColorStop(0.45, 'rgba(70,70,78,0.28)');
    grad.addColorStop(0.9, 'rgba(120,120,130,0.18)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.beginPath(); g.moveTo(cx, cy);
    g.arc(cx, cy, R, a0, a0 + 1.7); // a wide swept wedge
    g.closePath(); g.fill();
  }
  // A bright rim ring where the blade tips trace a circle — the most readable part of a prop blur.
  g.strokeStyle = 'rgba(180,180,195,0.35)'; g.lineWidth = 4;
  g.beginPath(); g.arc(cx, cy, R * 0.92, 0, Math.PI * 2); g.stroke();
  // Hub glint at the very centre.
  g.fillStyle = 'rgba(150,150,160,0.4)';
  g.beginPath(); g.arc(cx, cy, R * 0.06, 0, Math.PI * 2); g.fill();
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  _propTex = tex;
  return tex;
}

/** A prop-blur disc facing the pilot (+Z). Centred on the spinner; spun by the owner each frame. */
export function makePropDisc(radius: number): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    map: propBlurTexture(), transparent: true, depthWrite: false,
    side: THREE.DoubleSide, opacity: 0.9, blending: THREE.NormalBlending,
  });
  const disc = new THREE.Mesh(new THREE.CircleGeometry(radius, 40), mat);
  disc.userData.propDisc = true; // the plane's update() spins this
  return disc;
}

/** British/Allied roundel (used by the Sopwith). Returns a small group facing +Y. */
function roundel(d: number): THREE.Group {
  const g = new THREE.Group();
  const blue = new THREE.MeshBasicMaterial({ color: 0x1e3a7a, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const white = new THREE.MeshBasicMaterial({ color: 0xf0f0f0, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const red = new THREE.MeshBasicMaterial({ color: 0xc02c2c, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
  const a = new THREE.Mesh(new THREE.CircleGeometry(d, 32), blue);
  const b = new THREE.Mesh(new THREE.CircleGeometry(d * 0.62, 32), white);
  const c = new THREE.Mesh(new THREE.CircleGeometry(d * 0.3, 24), red);
  a.rotation.x = b.rotation.x = c.rotation.x = -Math.PI / 2;
  b.position.y = 0.01; c.position.y = 0.02;
  g.add(a, b, c);
  return g;
}

/* ================================================================== */
/* COCKPIT VIEWS (first person)                                        */
/* ================================================================== */
// Conventions match the Fokker cockpit: camera at ~(0, 0.6, -0.3), nose toward -Z.

export function makeAlbatrosCockpit(c: PlaneColors): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.MeshStandardMaterial({ color: c.cowling, roughness: 0.55, metalness: 0.15 });
  const wing = new THREE.MeshStandardMaterial({ color: c.wing, roughness: 0.7 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x6a6a66, roughness: 0.4, metalness: 0.6 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.9 });

  // Sleek tapered nose (inline engine) with a long pointed spinner — not a drum. Dropped LOW
  // (≈ y -0.82, like the Fokker) so the pilot looks OVER the cowl instead of into it.
  const deck = new THREE.Mesh(new THREE.CylinderGeometry(0.78, 1.0, 1.8, 24, 2), body);
  deck.rotation.x = Math.PI / 2; deck.position.set(0, -0.85, -1.7);
  g.add(deck);
  const spinner = new THREE.Mesh(new THREE.ConeGeometry(0.4, 1.2, 24), metal);
  spinner.rotation.x = -Math.PI / 2; spinner.position.set(0, -0.85, -2.9);
  g.add(spinner);
  const propDisc = makePropDisc(1.4);
  propDisc.position.set(0, -0.85, -3.0);
  g.add(propDisc);
  // Single upper wing across the top of the frame (biplane — one wing visible above).
  const upper = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.22, 1.7, 24, 2, 4), wing);
  upper.position.set(0, 1.5, -2.0);
  g.add(upper);
  // Cabane struts — lean IN at the bottom so they run down to the fuselage, not splay out.
  for (const dx of [-0.9, 0.9]) {
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.8, 8), wood);
    s.position.set(dx, 0.7, -1.9); s.rotation.z = dx > 0 ? -0.28 : 0.28;
    g.add(s);
  }
  cockpitGuns(g, -0.18, -1.45, 0.3);
  cockpitDash(g, body, -0.6);
  g.traverse(noShadow);
  return g;
}

export function makePfalzCockpit(c: PlaneColors): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.MeshStandardMaterial({ color: c.cowling, roughness: 0.4, metalness: 0.25 });
  const wing = new THREE.MeshStandardMaterial({ color: c.wing, roughness: 0.6 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x8a8e92, roughness: 0.35, metalness: 0.7 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.9 });

  // Rounded streamlined nose with a blunt rounded spinner (silver finish). Dropped LOW so the
  // pilot looks over the cowl, not into it.
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.85, 24, 18), body);
  nose.scale.set(1, 0.95, 1.7); nose.position.set(0, -0.85, -2.1);
  g.add(nose);
  const spinner = new THREE.Mesh(new THREE.SphereGeometry(0.4, 20, 14), metal);
  spinner.scale.set(1, 1, 1.4); spinner.position.set(0, -0.85, -3.0);
  g.add(spinner);
  const propDisc = makePropDisc(1.35);
  propDisc.position.set(0, -0.85, -3.1);
  g.add(propDisc);
  const upper = new THREE.Mesh(new THREE.BoxGeometry(7.0, 0.24, 1.8, 24, 2, 4), wing);
  upper.position.set(0, 1.5, -2.1);
  g.add(upper);
  for (const dx of [-0.9, 0.9]) {
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 1.8, 8), wood);
    s.position.set(dx, 0.7, -2.0); s.rotation.z = dx > 0 ? -0.28 : 0.28;
    g.add(s);
  }
  cockpitGuns(g, -0.18, -1.5, 0.3);
  cockpitDash(g, body, -0.6);
  g.traverse(noShadow);
  return g;
}

export function makeSopwithCockpit(c: PlaneColors): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.MeshStandardMaterial({ color: c.cowling, roughness: 0.7 });
  const wing = new THREE.MeshStandardMaterial({ color: c.wing, roughness: 0.75 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x4a4944, roughness: 0.4, metalness: 0.6 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.9 });

  // Rotary drum cowl like the Fokker, khaki, with a single upper wing + roundel. Dropped LOW so the
  // pilot looks over the cowl, not into it.
  const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.9, 1.4, 48, 4), metal);
  cowl.rotation.x = Math.PI / 2; cowl.position.set(0, -0.85, -1.8);
  g.add(cowl);
  const cowlFace = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 0.1, 48), body);
  cowlFace.rotation.x = Math.PI / 2; cowlFace.position.set(0, -0.85, -2.5);
  g.add(cowlFace);
  const propDisc = makePropDisc(1.45);
  propDisc.position.set(0, -0.85, -2.7);
  g.add(propDisc);
  const upper = new THREE.Mesh(new THREE.BoxGeometry(7.4, 0.22, 1.6, 24, 2, 4), wing);
  upper.position.set(0, 1.5, -2.0);
  g.add(upper);
  // Roundels on the visible wing top
  for (const dx of [-2.6, 2.6]) {
    const r = roundel(0.5); r.position.set(dx, 0.13, 0); upper.add(r);
  }
  for (const dx of [-0.9, 0.9]) {
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.8, 8), wood);
    s.position.set(dx, 0.7, -1.9); s.rotation.z = dx > 0 ? -0.28 : 0.28;
    g.add(s);
  }
  cockpitGuns(g, -0.18, -1.45, 0.3);
  cockpitDash(g, body, -0.6);
  g.traverse(noShadow);
  return g;
}

/* ------------------------------------------------------------------ */
/* Shared cockpit furniture — guns, instrument panel, gauges, coaming   */
/* (modelled from the WW1 fighter reference: twin breech MGs centre,    */
/*  curved varnished-wood panel with brass-rimmed dials, leather rim).  */
/* ------------------------------------------------------------------ */

const GUN_METAL = new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.45, metalness: 0.8 });
const GUN_BLUE = new THREE.MeshStandardMaterial({ color: 0x1a1c20, roughness: 0.5, metalness: 0.7 });
const LEATHER = new THREE.MeshStandardMaterial({ color: 0x5a4632, roughness: 0.85, metalness: 0.05 });
const BRASS = new THREE.MeshStandardMaterial({ color: 0xb38a3c, roughness: 0.4, metalness: 0.8 });
const PANEL_WOOD = new THREE.MeshStandardMaterial({ color: 0x4a3320, roughness: 0.55, metalness: 0.05 });
const GAUGE_FACE = new THREE.MeshStandardMaterial({ color: 0xddd8c8, roughness: 0.8 });
const GAUGE_TICK = new THREE.MeshBasicMaterial({ color: 0x161410 });
const NEEDLE_MAT = new THREE.MeshBasicMaterial({ color: 0xb02020 });

/** A single instrument dial: brass bezel, pale face, ring of ticks, a red needle. Faces +Z
 *  (toward the pilot). `r` is the dial radius. */
function dialGauge(r: number): THREE.Group {
  const d = new THREE.Group();
  const bezel = new THREE.Mesh(new THREE.TorusGeometry(r, r * 0.16, 10, 28), BRASS);
  d.add(bezel);
  const face = new THREE.Mesh(new THREE.CircleGeometry(r * 0.92, 28), GAUGE_FACE);
  face.position.z = -0.012; // slightly recessed behind the bezel ring
  d.add(face);
  // Tick marks around the rim.
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const tick = new THREE.Mesh(new THREE.BoxGeometry(r * 0.07, r * 0.2, 0.01), GAUGE_TICK);
    tick.position.set(Math.cos(a) * r * 0.72, Math.sin(a) * r * 0.72, 0.0);
    tick.rotation.z = a;
    d.add(tick);
  }
  // Needle, parked at a jaunty angle, plus a hub.
  const needle = new THREE.Mesh(new THREE.BoxGeometry(r * 0.09, r * 0.78, 0.012), NEEDLE_MAT);
  needle.position.set(0, r * 0.25, 0.01);
  needle.rotation.z = 0.6;
  d.add(needle);
  d.add(new THREE.Mesh(new THREE.CylinderGeometry(r * 0.12, r * 0.12, 0.02, 10).rotateX(Math.PI / 2), GAUGE_TICK));
  return d;
}

/** Twin belt-fed machine guns sitting on the cowl deck ahead of the pilot — the dominant
 *  foreground element of a WW1 fighter cockpit. Each: blued receiver, perforated jacket,
 *  barrel, and a ring-and-bead sight you look through. `y`/`z` place the breech ends. */
export function cockpitGuns(g: THREE.Group, y: number, z: number, spread: number) {
  for (const dx of [-spread, spread]) {
    const gun = new THREE.Group();
    const inboard = dx > 0 ? -1 : 1; // sign toward the fuselage centreline (for ammo feed side)

    // Receiver/breech — the big rectangular block nearest the pilot, with a bevelled rear face.
    const breech = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.42, 0.95, 2, 2, 4), GUN_BLUE);
    breech.position.set(0, 0, 0.1);
    gun.add(breech);
    // Rounded top cover with a raised rib (the Spandau's hinged feed cover).
    const cover = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.16, 0.8), GUN_METAL);
    cover.position.set(0, 0.27, 0.08);
    gun.add(cover);
    const rib = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.7), GUN_BLUE);
    rib.position.set(0, 0.37, 0.06);
    gun.add(rib);
    // Charging handle — a small curved lever on the side of the receiver.
    const handle = new THREE.Mesh(new THREE.TorusGeometry(0.08, 0.018, 8, 12, Math.PI), GUN_METAL);
    handle.rotation.y = Math.PI / 2; handle.position.set(inboard * 0.2, 0.05, 0.34);
    gun.add(handle);

    // Perforated cooling jacket — slotted sleeve over the barrel root: a tube + a ring of
    // cooling holes (short dark cylinders set into the surface) for the classic Maxim look.
    const jacket = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 1.5, 20), GUN_METAL);
    jacket.rotation.x = Math.PI / 2; jacket.position.set(0, 0.02, -1.0);
    gun.add(jacket);
    for (let rowZ = -1.55; rowZ <= -0.45; rowZ += 0.22) {
      for (let h = 0; h < 6; h++) {
        const a = (h / 6) * Math.PI * 2;
        const hole = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.04, 6), GUN_BLUE);
        hole.position.set(Math.cos(a) * 0.13, 0.02 + Math.sin(a) * 0.13, rowZ);
        hole.rotation.x = Math.PI / 2; hole.rotation.z = a; // lay flat against the tube wall
        hole.lookAt(0, 0.02, rowZ);
        gun.add(hole);
      }
    }
    // Inner barrel + muzzle protruding from the jacket.
    const muzzle = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.36, 12), GUN_BLUE);
    muzzle.rotation.x = Math.PI / 2; muzzle.position.set(0, 0.02, -1.88);
    gun.add(muzzle);

    // Ammo feed: a block on the inboard side + a short ribbed cartridge belt drooping from it.
    const feed = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.3, 0.4), GUN_METAL);
    feed.position.set(inboard * 0.22, -0.04, 0.18);
    gun.add(feed);
    for (let i = 0; i < 6; i++) {
      const link = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.05, 0.05), BRASS);
      link.position.set(inboard * (0.3 + i * 0.012), -0.18 - i * 0.05, 0.18 + i * 0.015);
      link.rotation.z = inboard * 0.2;
      gun.add(link);
    }

    // Ring-and-bead sight: brass ring at the breech + a front blade out near the muzzle, so you
    // sight ALONG the barrel (matches the line of fire).
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.012, 8, 18), BRASS);
    ring.position.set(0, 0.34, -0.35);
    gun.add(ring);
    const cross = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.006, 0.006), BRASS);
    cross.position.copy(ring.position); gun.add(cross);
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.1, 0.012), GUN_METAL);
    blade.position.set(0, 0.16, -1.7);
    gun.add(blade);

    gun.position.set(dx, y, z);
    gun.rotation.x = -0.04; // muzzles tip very slightly down toward convergence
    g.add(gun);
  }
}

/** Shared instrument panel: a curved varnished-wood dashboard with brass-rimmed dial gauges and
 *  a leather coaming lip along the top, filling the lower frame. Tuned via `y` (panel height). */
export function cockpitDash(g: THREE.Group, _body: THREE.Material, y = -0.4) {
  // Curved wood panel — a shallow arc of segments so it reads as a coaming, not a flat slab.
  const panel = new THREE.Group();
  const segs = 9, width = 2.6, segW = width / segs;
  for (let i = 0; i < segs; i++) {
    const t = (i / (segs - 1)) * 2 - 1;           // -1..1 across the panel
    const seg = new THREE.Mesh(new THREE.BoxGeometry(segW * 1.04, 0.5, 0.22), PANEL_WOOD);
    seg.position.set(t * (width / 2 - segW / 2), -Math.abs(t) * 0.12, t * t * 0.28); // arc back at the edges
    seg.rotation.y = -t * 0.5;
    panel.add(seg);
  }
  panel.position.set(0, y, -0.92);
  g.add(panel);
  // Leather coaming lip riding the top edge of the panel.
  const lip = new THREE.Mesh(new THREE.TorusGeometry(1.25, 0.07, 8, 28, Math.PI), LEATHER);
  lip.rotation.x = Math.PI / 2; lip.rotation.z = Math.PI;
  lip.scale.set(1.05, 1, 0.5);
  lip.position.set(0, y + 0.26, -0.86);
  g.add(lip);
  // Three brass dial gauges across the panel, facing the pilot.
  for (const dx of [-0.62, 0, 0.62]) {
    const dial = dialGauge(dx === 0 ? 0.2 : 0.16);
    dial.position.set(dx, y + 0.04, -0.79);
    g.add(dial);
  }
}

function noShadow(o: THREE.Object3D) { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; }

/* ================================================================== */
/* FULL EXTERIOR MODELS (shown on death)                               */
/* ================================================================== */

/** Albatros D.Va — biplane, sleek plywood fuselage, pointed spinner, inline engine. */
export function makeAlbatrosFull(c: PlaneColors): THREE.Group {
  return biplaneExterior({
    fuse: c.cowling, wing: c.wing,
    spinner: 'cone', drumCowl: false,
    roundels: false, crosses: true,
    fuseLen: 5.2, fuseR: 0.6,
  });
}

/** Pfalz D.IIIa — biplane, very rounded silver fuselage, blunt spinner. */
export function makePfalzFull(c: PlaneColors): THREE.Group {
  return biplaneExterior({
    fuse: c.cowling, wing: c.wing,
    spinner: 'round', drumCowl: false,
    roundels: false, crosses: true,
    fuseLen: 5.0, fuseR: 0.65,
  });
}

/** Sopwith Snipe — biplane, rotary drum cowl, khaki, British roundels. */
export function makeSopwithFull(c: PlaneColors): THREE.Group {
  return biplaneExterior({
    fuse: c.cowling, wing: c.wing,
    spinner: 'none', drumCowl: true,
    roundels: true, crosses: false,
    fuseLen: 4.8, fuseR: 0.55,
  });
}

interface BiplaneOpts {
  fuse: number; wing: number;
  spinner: 'cone' | 'round' | 'none';
  drumCowl: boolean;
  roundels: boolean; crosses: boolean;
  fuseLen: number; fuseR: number;
}

function biplaneExterior(o: BiplaneOpts): THREE.Group {
  const g = new THREE.Group();
  const fuseMat = new THREE.MeshStandardMaterial({ color: o.fuse, roughness: 0.7 });
  const wingMat = new THREE.MeshStandardMaterial({ color: o.wing, roughness: 0.8 });
  const trimMat = new THREE.MeshStandardMaterial({ color: 0x2a2014, roughness: 0.85 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x4a4944, roughness: 0.4, metalness: 0.65 });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.95 });

  // Fuselage — tapered cylinder, nose toward -Z.
  const body = new THREE.Mesh(new THREE.CylinderGeometry(o.fuseR, o.fuseR * 0.45, o.fuseLen, 20, 6), fuseMat);
  body.rotation.x = Math.PI / 2; body.position.set(0, 0, 0.4);
  g.add(body);

  // Engine front: drum cowl (rotary) or pointed/round spinner (inline).
  if (o.drumCowl) {
    const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.66, 0.7, 40, 2), metal);
    cowl.rotation.x = Math.PI / 2; cowl.position.set(0, 0, -o.fuseLen / 2 - 0.1);
    g.add(cowl);
  } else if (o.spinner === 'cone') {
    const sp = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.3, 24), metal);
    sp.rotation.x = -Math.PI / 2; sp.position.set(0, 0, -o.fuseLen / 2 - 0.65);
    g.add(sp);
  } else if (o.spinner === 'round') {
    const sp = new THREE.Mesh(new THREE.SphereGeometry(0.45, 20, 14), metal);
    sp.scale.set(1, 1, 1.5); sp.position.set(0, 0, -o.fuseLen / 2 - 0.4);
    g.add(sp);
  }

  // Propeller + disc
  const prop = makeProp(0.2, trimMat, metal);
  prop.position.set(0, 0, -o.fuseLen / 2 - 0.9);
  g.add(prop); g.userData.prop = prop;

  // Tail cone
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.3, 1.0, 16), fuseMat);
  tail.rotation.x = Math.PI / 2; tail.position.set(0, 0, o.fuseLen / 2 + 0.5);
  g.add(tail);

  // Two staggered wings (biplane)
  const upper = airfoilWing(8.4, 1.5, 0.2, wingMat, trimMat);
  upper.position.set(0, 1.25, -0.4);
  g.add(upper);
  const lower = airfoilWing(7.8, 1.45, 0.18, wingMat, trimMat);
  lower.position.set(0, -0.55, 0.2);
  g.add(lower);

  // Interplane struts (outboard pairs)
  for (const dx of [-2.6, 2.6]) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.9, 0.14, 2, 6, 2), trimMat);
    s.position.set(dx, 0.35, -0.1);
    g.add(s);
  }

  // Tail surfaces
  const hstab = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.12, 1.0, 12, 2, 4), wingMat);
  hstab.position.set(0, 0.1, o.fuseLen / 2 + 0.3);
  g.add(hstab);
  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.0, 1.0, 2, 6, 4), wingMat);
  fin.position.set(0, 0.55, o.fuseLen / 2 + 0.5);
  g.add(fin);

  // Cockpit hole + pilot
  const pit = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.5, 20), new THREE.MeshStandardMaterial({ color: 0x120c08, roughness: 1 }));
  pit.rotation.x = Math.PI / 2; pit.scale.set(1, 1, 1.4); pit.position.set(0, 0.4, 0.3);
  g.add(pit);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 24, 16), new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.9 }));
  head.position.set(0, 0.62, 0.35);
  g.add(head);

  // Landing gear
  for (const dx of [-1.0, 1.0]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.18, 24), rubber);
    wheel.rotation.z = Math.PI / 2; wheel.position.set(dx, -1.15, -0.3);
    g.add(wheel);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.9, 8), trimMat);
    leg.position.set(dx * 0.6, -0.78, -0.3); leg.rotation.z = dx > 0 ? 0.35 : -0.35;
    g.add(leg);
  }

  // National markings
  if (o.roundels) {
    for (const dx of [-2.7, 2.7]) { const r = roundel(0.6); r.position.set(dx, 0.12, -0.4); upper.add(r); }
    for (const dx of [-2.5, 2.5]) { const r = roundel(0.55); r.rotation.x = Math.PI; r.position.set(dx, -0.12, 0.2); lower.add(r); }
  }
  if (o.crosses) {
    const black = new THREE.MeshBasicMaterial({ color: 0x0b0b0b, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
    const white = new THREE.MeshBasicMaterial({ color: 0xe6e6e6, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    const cross = (parent: THREE.Object3D, x: number, y: number, flip: boolean) => {
      const sq = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.8), white);
      const cv = new THREE.Mesh(new THREE.PlaneGeometry(0.24, 0.74), black);
      const ch = new THREE.Mesh(new THREE.PlaneGeometry(0.74, 0.24), black);
      const rx = flip ? Math.PI / 2 : -Math.PI / 2;
      for (const m of [sq, cv, ch]) { m.rotation.x = rx; m.position.set(x, y, 0); }
      cv.position.y += flip ? -0.01 : 0.01; ch.position.y += flip ? -0.01 : 0.01;
      parent.add(sq, cv, ch);
    };
    cross(upper, -2.7, 0.12, false); cross(upper, 2.7, 0.12, false);
    cross(lower, -2.4, -0.12, true); cross(lower, 2.4, -0.12, true);
  }

  g.traverse((o2) => { (o2 as THREE.Mesh).castShadow = false; });
  return g;
}
